/**
 * Sound effects with Web Audio (SPEC §15: CC0 or generated). Audio starts after the first user
 * interaction. Effects with recorded takes (generated once, in `public/audio/sfx/{name}-{take}.mp3`)
 * play one of them with a small random change of pitch and volume, so repeats never sound identical;
 * the rest are short recipes of oscillators and filtered noise.
 */
type Sfx =
  | 'drum'
  | 'mend'
  | 'sanctuary'
  | 'dawn'
  | 'bolt'
  | 'rock'
  | 'boom'
  | 'arcane'
  | 'bell'
  // The Order's great bell (El Párpado 4): deep, old and cracked.
  | 'greatBell'
  | 'hit'
  | 'slash'
  | 'die'
  | 'keep'
  | 'build'
  | 'click'
  | 'skill'
  | 'oath'
  | 'fire'
  | 'frost'
  | 'down'
  | 'horn'
  | 'victory'
  | 'defeat'
  | 'crumble'
  | 'curse'
  | 'die-goblin'
  | 'die-wolf'
  | 'die-skeleton'
  | 'die-wraith'
  | 'die-gargoyle'
  | 'die-slime'
  | 'die-stalker'
  | 'boss-witch'
  | 'boss-shell'
  | 'arrow'
  | 'turret'
  | 'shout'
  | 'shieldBash'
  | 'net'
  | 'arrowRain'
  | 'repair'
  | 'storm'
  | 'ingenuity'
  // The learned skills and the reactions (docs/PODERES.md §5).
  | 'decoy'
  | 'frostOrb'
  | 'hawk'
  | 'totem'
  | 'hammer'
  | 'clanRoar'
  | 'drone'
  | 'wasps'
  | 'shatter'
  | 'combust'
  | 'lowBlow'
  | 'chainLightning'
  | 'poisonArrow'
  | 'faithRay'
  | 'throwingAxe'
  | 'blindingLight'
  | 'spikeMine'
  | 'charge'
  | 'deepRoot'
  | 'guardBanner'
  | 'blizzard'
  | 'titanArrow'
  | 'dawnJudgment'
  | 'holyRampart'
  | 'fieldMortar'
  | 'plague'
  | 'grapple'
  | 'tuneUp'
  | 'frostLance'
  | 'piercingShot'
  | 'renew'
  | 'brutalBlow'
  | 'holySeal'
  | 'briarLash'
  | 'defyShout'
  | 'teslaCoil'
  | 'blink'
  | 'snare'
  | 'sharedPrayer'
  | 'bloodletting'
  | 'intervene'
  | 'thornWall'
  | 'lastStand'
  | 'clockHound'
  | 'fireRain'
  | 'eagleEye'
  | 'intercession'
  | 'ancestralFury'
  | 'avatarOfLight'
  | 'forestAwakening'
  | 'shieldSlam'
  | 'shieldWall'
  | 'shieldEcho'
  | 'flamethrower'
  | 'ironBarricade'
  | 'fortifyAll'
  | 'acidDart'
  | 'shadowChains'
  | 'dreamRift'
  | 'disengage'
  | 'signalFlare'
  | 'hiddenShot'
  | 'piercingLight'
  | 'cleanse'
  | 'choir'
  | 'kick'
  | 'terrify'
  | 'duel'
  | 'atone'
  | 'blessWeapon'
  | 'sacrifice'
  | 'sporeCloud'
  | 'wolfForm'
  | 'rootQuake'
  // Signals between players (signals.ts).
  | 'sigAttack'
  | 'sigDefend'
  | 'sigDanger'
  | 'sigComing'
  | 'sigHelp';

export type { Sfx };

/** Recorded takes per effect. */
export const SAMPLES: Partial<Record<Sfx, string[]>> = {
  arcane: ['a', 'b'],
  arrow: ['a', 'b'],
  arrowRain: ['a', 'b'],
  bell: ['a', 'b'],
  bolt: ['a', 'b'],
  boom: ['a', 'b'],
  'boss-shell': ['a', 'b'],
  'boss-witch': ['a', 'b'],
  build: ['a', 'b'],
  click: ['a'],
  crumble: ['a', 'b'],
  curse: ['a', 'b'],
  defeat: ['a', 'b'],
  'die-gargoyle': ['a', 'b'],
  'die-goblin': ['a', 'b'],
  'die-skeleton': ['a', 'b'],
  'die-slime': ['a', 'b'],
  'die-stalker': ['a', 'b'],
  'die-wolf': ['a', 'b'],
  'die-wraith': ['a', 'b'],
  down: ['a', 'b'],
  fire: ['a', 'b'],
  frost: ['a', 'b'],
  hit: ['a', 'b'],
  horn: ['a', 'b'],
  ingenuity: ['a', 'b'],
  keep: ['a', 'b'],
  net: ['a', 'b'],
  oath: ['a', 'b'],
  repair: ['a', 'b'],
  rock: ['a', 'b'],
  shieldBash: ['a', 'b'],
  shout: ['a', 'b'],
  skill: ['a', 'b'],
  slash: ['a', 'b'],
  storm: ['a', 'b'],
  turret: ['a', 'b'],
  victory: ['a', 'b'],
  decoy: ['a', 'b'],
  frostOrb: ['a', 'b'],
  hawk: ['a', 'b'],
  totem: ['a', 'b'],
  hammer: ['a', 'b'],
  clanRoar: ['a', 'b'],
  drone: ['a', 'b'],
  wasps: ['a', 'b'],
  shatter: ['a', 'b'],
  combust: ['a', 'b'],
  lowBlow: ['a', 'b'],
  chainLightning: ['a', 'b'],
  poisonArrow: ['a', 'b'],
  faithRay: ['a', 'b'],
  throwingAxe: ['a', 'b'],
  blindingLight: ['a', 'b'],
  spikeMine: ['a', 'b'],
  charge: ['a', 'b'],
  deepRoot: ['a', 'b'],
  guardBanner: ['a', 'b'],
  blizzard: ['a', 'b'],
  titanArrow: ['a', 'b'],
  dawnJudgment: ['a', 'b'],
  holyRampart: ['a', 'b'],
  fieldMortar: ['a'],
  plague: ['a', 'b'],
  grapple: ['a', 'b'],
  tuneUp: ['a', 'b'],
  frostLance: ['a', 'b'],
  piercingShot: ['a', 'b'],
  renew: ['a', 'b'],
  brutalBlow: ['a', 'b'],
  holySeal: ['a', 'b'],
  briarLash: ['a', 'b'],
  defyShout: ['a', 'b'],
  teslaCoil: ['a', 'b'],
  blink: ['a', 'b'],
  snare: ['a', 'b'],
  sharedPrayer: ['a', 'b'],
  bloodletting: ['a', 'b'],
  intervene: ['a', 'b'],
  thornWall: ['a', 'b'],
  lastStand: ['a', 'b'],
  clockHound: ['a', 'b'],
  fireRain: ['a', 'b'],
  eagleEye: ['a', 'b'],
  intercession: ['a', 'b'],
  ancestralFury: ['a', 'b'],
  avatarOfLight: ['a', 'b'],
  forestAwakening: ['a', 'b'],
  shieldSlam: ['a', 'b'],
  shieldWall: ['a', 'b'],
  shieldEcho: ['a', 'b'],
  flamethrower: ['a', 'b'],
  ironBarricade: ['a', 'b'],
  fortifyAll: ['a', 'b'],
  acidDart: ['a', 'b'],
  shadowChains: ['a', 'b'],
  dreamRift: ['a', 'b'],
  disengage: ['a', 'b'],
  signalFlare: ['a', 'b'],
  hiddenShot: ['a', 'b'],
  piercingLight: ['a', 'b'],
  cleanse: ['a', 'b'],
  choir: ['a', 'b'],
  kick: ['a', 'b'],
  terrify: ['a', 'b'],
  duel: ['a', 'b'],
  atone: ['a', 'b'],
  blessWeapon: ['a', 'b'],
  sacrifice: ['a', 'b'],
  sporeCloud: ['a', 'b'],
  wolfForm: ['a', 'b'],
  rootQuake: ['a', 'b'],
};
/** Random variation per play: ± pitch (as playback rate) and the lowest volume factor. */
const PITCH_SPREAD = 0.07;
const VOLUME_FLOOR = 0.8;
/** Recorded takes are peak-normalized to -1 dB; this sets them under the music. */
const SAMPLE_GAIN = 0.9;

class Audio {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  sfxGain: GainNode | null = null;
  musicGain: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private last = new Map<Sfx, number>();
  private samples = new Map<Sfx, AudioBuffer[]>();
  enabled = true;
  musicOn = true;
  sfxVol = 1;
  musicVol = 1;

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AC();
    } catch {
      return;
    }
    const c = this.ctx;
    this.master = c.createGain();
    this.master.gain.value = 0.8;
    this.master.connect(c.destination);
    this.sfxGain = c.createGain();
    this.sfxGain.gain.value = this.enabled ? 0.55 * this.sfxVol : 0;
    this.sfxGain.connect(this.master);
    this.musicGain = c.createGain();
    this.musicGain.gain.value = this.musicOn ? 0.35 * this.musicVol : 0;
    this.musicGain.connect(this.master);
    const len = c.sampleRate;
    this.noiseBuf = c.createBuffer(1, len, c.sampleRate);
    const d = this.noiseBuf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    void this.loadSamples();
  }

  private async loadSamples(): Promise<void> {
    const c = this.ctx as AudioContext;
    await Promise.all(
      (Object.entries(SAMPLES) as [Sfx, string[]][]).map(async ([name, takes]) => {
        const bufs: AudioBuffer[] = [];
        for (const take of takes) {
          try {
            const res = await fetch(`${import.meta.env.BASE_URL}audio/sfx/${name}-${take}.mp3`);
            if (!res.ok) continue;
            bufs.push(await c.decodeAudioData(await res.arrayBuffer()));
          } catch {
            /* missing or undecodable: the synth recipe stays */
          }
        }
        if (bufs.length) this.samples.set(name, bufs);
      }),
    );
  }

  /** Plays a recorded take, if there is one: random take, pitch within ±7 %, volume 80–100 %. */
  private sample(name: Sfx, t: number, v: number): boolean {
    const bufs = this.samples.get(name);
    if (!bufs) return false;
    const c = this.ctx as AudioContext;
    const src = c.createBufferSource();
    src.buffer = bufs[Math.floor(Math.random() * bufs.length)] as AudioBuffer;
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * PITCH_SPREAD;
    const g = c.createGain();
    g.gain.value = v * SAMPLE_GAIN * (VOLUME_FLOOR + Math.random() * (1 - VOLUME_FLOOR));
    src.connect(g).connect(this.sfxGain as GainNode);
    src.start(t);
    return true;
  }

  setSfx(on: boolean, vol = this.sfxVol): void {
    this.enabled = on;
    this.sfxVol = vol;
    if (this.sfxGain) this.sfxGain.gain.value = on ? 0.55 * vol : 0;
  }

  setMusic(on: boolean, vol = this.musicVol): void {
    this.musicOn = on;
    this.musicVol = vol;
    if (this.musicGain) this.musicGain.gain.setTargetAtTime(on ? 0.35 * vol : 0, this.ctx?.currentTime ?? 0, 0.1);
  }

  private noise(t: number, dur: number, freq: number, q: number, gain: number, type: BiquadFilterType = 'bandpass'): void {
    const c = this.ctx as AudioContext;
    const src = c.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = c.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = c.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.sfxGain as GainNode);
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.05);
  }

  private tone(t: number, dur: number, f0: number, f1: number, type: OscillatorType, gain: number, attack = 0.005): void {
    const c = this.ctx as AudioContext;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.sfxGain as GainNode);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  play(name: Sfx, volume = 1): void {
    if (!this.ctx || !this.enabled || this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;
    // Rate-limit identical sounds so big waves don't turn into noise.
    const minGap: Partial<Record<Sfx, number>> = { hit: 0.05, die: 0.06, bolt: 0.06, arcane: 0.05, slash: 0.06, keep: 0.12, arrow: 0.06, turret: 0.06 };
    const prev = this.last.get(name) ?? -1;
    if (now - prev < (minGap[name] ?? 0.02)) return;
    this.last.set(name, now);
    const v = volume;
    const t = now + 0.005;
    // Development hook: a page can collect what played (recording videos with sound).
    (window as unknown as { __sfxLog?: { name: string; v: number; at: number }[] }).__sfxLog?.push({ name, v, at: performance.now() });
    if (this.sample(name, t, v)) return;
    switch (name) {
      case 'bolt':
        this.tone(t, 0.12, 180, 70, 'triangle', 0.25 * v);
        this.noise(t, 0.08, 2400, 3, 0.15 * v);
        break;
      case 'rock':
        this.noise(t, 0.25, 300, 1, 0.3 * v, 'lowpass');
        this.tone(t, 0.2, 120, 50, 'sine', 0.25 * v);
        break;
      case 'boom':
        this.noise(t, 0.5, 180, 0.7, 0.5 * v, 'lowpass');
        this.tone(t, 0.45, 90, 30, 'sine', 0.45 * v);
        break;
      case 'arcane':
        this.tone(t, 0.18, 1400, 500, 'sawtooth', 0.06 * v);
        this.tone(t, 0.22, 900, 1800, 'sine', 0.08 * v);
        break;
      case 'bell':
        for (const [f, g] of [[220, 0.3], [440, 0.12], [553, 0.08], [880, 0.05]] as const) this.tone(t, 2.2, f, f * 0.995, 'sine', g * v, 0.004);
        break;
      case 'greatBell':
        // A huge bronze bell, cracked: a low hum, inharmonic partials a hair apart (the crack's beating), a dull thud.
        for (const [f, g, d] of [[73, 0.34, 4.5], [74.6, 0.2, 4.2], [146, 0.18, 3.2], [176, 0.14, 2.6], [219, 0.12, 2.4], [292, 0.06, 1.6], [397, 0.04, 1.1]] as const) this.tone(t, d, f, f * 0.992, 'sine', g * v, 0.006);
        this.noise(t, 0.18, 260, 0.9, 0.22 * v, 'lowpass');
        break;
      case 'hit':
        this.noise(t, 0.06, 1600, 2, 0.12 * v);
        break;
      case 'slash':
        this.noise(t, 0.12, 3200, 1.5, 0.18 * v, 'highpass');
        this.tone(t, 0.08, 900, 300, 'square', 0.04 * v);
        break;
      case 'die':
        this.noise(t, 0.35, 500, 0.8, 0.18 * v, 'lowpass');
        this.tone(t, 0.3, 300, 80, 'sawtooth', 0.05 * v);
        break;
      case 'keep':
        this.tone(t, 0.4, 70, 40, 'sine', 0.4 * v);
        this.noise(t, 0.25, 220, 1, 0.2 * v, 'lowpass');
        break;
      case 'build':
        for (let i = 0; i < 3; i++) this.noise(t + i * 0.09, 0.07, 900 + i * 200, 4, 0.22 * v);
        break;
      case 'click':
        this.tone(t, 0.05, 900, 600, 'triangle', 0.08 * v);
        break;
      case 'skill':
        this.tone(t, 0.25, 260, 520, 'triangle', 0.12 * v);
        this.noise(t, 0.2, 1200, 1, 0.08 * v);
        break;
      case 'oath':
        for (const f of [196, 247, 294, 392]) this.tone(t, 1.2, f, f, 'triangle', 0.08 * v, 0.05);
        break;
      case 'fire':
        this.noise(t, 0.6, 700, 0.6, 0.3 * v, 'lowpass');
        break;
      case 'frost':
        this.tone(t, 0.5, 2400, 1200, 'sine', 0.06 * v);
        this.noise(t, 0.4, 5000, 2, 0.08 * v, 'highpass');
        break;
      case 'down':
        this.tone(t, 0.6, 330, 110, 'triangle', 0.15 * v);
        break;
      case 'drum':
        // A war drum: a low thump with a skin slap on top.
        this.tone(t, 0.45, 120, 48, 'sine', 0.5 * v, 0.003);
        this.noise(t, 0.12, 380, 1.2, 0.22 * v, 'lowpass');
        break;
      case 'horn':
        this.tone(t, 0.9, 147, 147, 'sawtooth', 0.09 * v, 0.08);
        this.tone(t, 0.9, 220, 220, 'sawtooth', 0.05 * v, 0.08);
        break;
      // Signals: short and each its own, heard over a fight without covering it.
      case 'sigAttack':
        this.tone(t, 0.09, 740, 1110, 'triangle', 0.11 * v);
        this.tone(t + 0.07, 0.12, 1110, 1480, 'triangle', 0.09 * v);
        this.noise(t, 0.05, 3200, 4, 0.05 * v);
        break;
      case 'sigDefend':
        this.tone(t, 0.28, 392, 392, 'sine', 0.16 * v, 0.01);
        this.tone(t + 0.11, 0.32, 294, 294, 'sine', 0.14 * v, 0.01);
        break;
      case 'sigDanger':
        for (let i = 0; i < 2; i++) {
          this.tone(t + i * 0.2, 0.1, 988, 988, 'square', 0.035 * v);
          this.tone(t + i * 0.2 + 0.1, 0.1, 740, 740, 'square', 0.035 * v);
        }
        break;
      case 'sigComing':
        this.tone(t, 0.22, 523, 784, 'triangle', 0.1 * v, 0.01);
        break;
      case 'sigHelp':
        for (let i = 0; i < 2; i++) {
          this.tone(t + i * 0.32, 0.28, 196, 207, 'sawtooth', 0.08 * v, 0.03);
          this.tone(t + i * 0.32, 0.28, 294, 311, 'sawtooth', 0.05 * v, 0.03);
        }
        this.tone(t, 0.7, 1175, 1175, 'sine', 0.05 * v, 0.01);
        break;
      case 'victory':
        [262, 330, 392, 523].forEach((f, i) => this.tone(t + i * 0.16, 0.9, f, f, 'triangle', 0.12 * v, 0.02));
        break;
      case 'defeat':
        [220, 208, 196, 147].forEach((f, i) => this.tone(t + i * 0.28, 1.1, f, f * 0.98, 'sawtooth', 0.06 * v, 0.03));
        break;
      case 'crumble':
        for (let i = 0; i < 5; i++) this.noise(t + i * 0.07, 0.25, 250 + i * 60, 0.8, 0.25 * v, 'lowpass');
        break;
      case 'mend':
        // A soft rising chime with air on top.
        [784, 988, 1175, 1568].forEach((f, i) => this.tone(t + i * 0.045, 0.7, f, f, 'sine', 0.07 * v, 0.01));
        this.noise(t, 0.5, 7000, 1.5, 0.05 * v, 'highpass');
        break;
      case 'sanctuary':
        // A held chord, like voices in a stone chapel, with a shimmer.
        for (const f of [262, 330, 392, 523]) this.tone(t, 1.6, f, f, 'sine', 0.07 * v, 0.25);
        for (const f of [1046, 1318]) this.tone(t + 0.15, 1.2, f, f * 1.003, 'triangle', 0.02 * v, 0.2);
        this.noise(t, 1.2, 6000, 2, 0.04 * v, 'highpass');
        break;
      case 'dawn':
        // Sunrise: a swelling major chord over a low bell, and a rush of air.
        for (const [f, g] of [[131, 0.14], [196, 0.1], [262, 0.1], [330, 0.08], [392, 0.07], [523, 0.05], [659, 0.04]] as const) this.tone(t, 2.6, f, f, 'sine', g * v, 0.35);
        for (const [f, g] of [[110, 0.22], [220, 0.08], [277, 0.05]] as const) this.tone(t, 3, f, f * 0.997, 'sine', g * v, 0.004);
        this.noise(t, 1.4, 1800, 0.5, 0.12 * v, 'bandpass');
        [1568, 1976, 2349].forEach((f, i) => this.tone(t + 0.35 + i * 0.08, 1.4, f, f, 'sine', 0.025 * v, 0.02));
        break;
      case 'curse':
        this.tone(t, 0.9, 140, 70, 'sawtooth', 0.08 * v);
        this.tone(t, 0.9, 147, 73, 'sawtooth', 0.08 * v);
        break;
    }
  }
}

export const audio = new Audio();

/** Unlock on the first gesture anywhere. */
export const installAudioUnlock = (onUnlock: () => void = () => undefined): void => {
  const go = (): void => {
    audio.unlock();
    // resume() is async on some browsers: give the context a moment, then start the music.
    window.setTimeout(onUnlock, 120);
  };
  window.addEventListener('pointerdown', go, { passive: true });
  window.addEventListener('keydown', go);
};
